import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateLocale } from '../utils/dateFormat';
import { getConsent, saveConsent, clearConsent } from '../services/CookieConsentService';
import { Shield, ArrowLeft, Check, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

/**
 * P1-001: Cookie Preferences Settings Page — the canonical consent-preference
 * surface at `/cookie-settings` (the published Cookie Policy lives separately
 * at `/legal/cookie_policy`).
 *
 * TREATX-COOKIE-SETTINGS-POLICY-ALIGNMENT-01
 * Categories and wording mirror the published Cookie Policy §3/§4/§7:
 * Strictly necessary (no consent), Functional – Google sign-in (no consent),
 * Analytics (explicit consent, off by default, withdrawable). The Policy
 * states TreatX uses no advertising/retargeting/social-media technology, so no
 * Marketing category is offered; stored records keep `marketing: false` for
 * backward compatibility with historical consent evidence.
 */
const CookieSettingsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [consent, setConsent] = useState(null);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const current = getConsent();
    setConsent(current);
    if (current?.categories) {
      setPreferences({
        essential: true,
        analytics: current.categories.analytics || false,
        marketing: false,
      });
    }
  }, []);

  const handleSave = useCallback(() => {
    saveConsent(preferences, 'settings');
    setConsent(getConsent());
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }, [preferences]);

  const handleReset = useCallback(() => {
    clearConsent();
    setConsent(null);
    setPreferences({ essential: true, analytics: false, marketing: false });
  }, []);

  const categories = [
    {
      id: 'essential',
      label: t('cookies.settings.necessaryLabel', 'Strictly necessary cookies'),
      description: t('cookies.settings.necessaryDescription', ''),
      control: 'locked',
    },
    {
      id: 'functional',
      label: t('cookies.settings.functionalLabel', 'Functional cookies (Google sign-in)'),
      description: t('cookies.settings.functionalDescription', ''),
      control: 'informational',
    },
    {
      id: 'analytics',
      label: t('cookies.settings.analyticsLabel', 'Analytics cookies'),
      description: t('cookies.settings.analyticsDescription', ''),
      control: 'optional',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cookie-settings-page">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors"
          data-testid="cookie-settings-back"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('cookies.settings.back', 'Back')}
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <Shield className="h-5 w-5 text-teal-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="cookie-settings-title">
            {t('cookies.settings.title', 'Cookie Preferences')}
          </h1>
        </div>
        <p className="text-sm text-slate-500 mb-8 ml-[52px]">
          {t('cookies.settings.intro', '')}
        </p>

        {/* Consent Status */}
        {consent && (
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6 ml-[52px]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">
                  {t('cookies.settings.lastUpdated', 'Last updated')}
                </p>
                <p className="text-sm font-medium text-slate-700" data-testid="cookie-settings-updated-at">
                  {formatDateLocale(consent.timestamp)}
                </p>
              </div>
              <button
                onClick={handleReset}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                data-testid="cookie-reset-btn"
              >
                <RotateCcw className="h-3 w-3" />
                {t('cookies.settings.reset', 'Reset')}
              </button>
            </div>
          </div>
        )}

        {/* Categories — mirror of the published Cookie Policy */}
        <div className="space-y-3 ml-[52px]">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:border-slate-300 transition-colors"
              data-testid={`cookie-settings-category-${cat.id}`}
            >
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className="text-sm font-semibold text-slate-900">{cat.label}</span>
                {cat.control === 'locked' && (
                  <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                    data-testid="cookie-settings-badge-essential">
                    {t('cookies.settings.alwaysActive', 'Always Active')}
                  </span>
                )}
                {cat.control === 'informational' && (
                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                    data-testid="cookie-settings-badge-functional">
                    {t('cookies.settings.noConsentRequired', 'No consent required')}
                  </span>
                )}
                {cat.control === 'optional' && (
                  <button
                    role="switch"
                    aria-checked={preferences[cat.id]}
                    aria-label={cat.label}
                    onClick={() => setPreferences((p) => ({ ...p, [cat.id]: !p[cat.id] }))}
                    className={`h-6 w-11 shrink-0 rounded-full relative transition-colors ${
                      preferences[cat.id] ? 'bg-teal-500' : 'bg-slate-300'
                    }`}
                    data-testid={`cookie-settings-toggle-${cat.id}`}
                  >
                    <div
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        preferences[cat.id] ? 'translate-x-[22px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{cat.description}</p>
            </div>
          ))}
        </div>

        {/* Save */}
        <div className="flex items-center gap-3 mt-6 ml-[52px]">
          <Button
            onClick={handleSave}
            className="bg-teal-600 hover:bg-teal-500 text-white text-sm px-6"
            data-testid="cookie-settings-save-btn"
          >
            {saved ? (
              <>
                <Check className="h-4 w-4 mr-1.5" />
                {t('cookies.settings.saved', 'Saved')}
              </>
            ) : (
              t('cookies.settings.save', 'Save Preferences')
            )}
          </Button>
        </div>

        {/* Canonical policy links — the published document, not a copy */}
        <div className="mt-8 pt-6 border-t border-slate-200 ml-[52px]">
          <p className="text-xs text-slate-400">
            {t('cookies.settings.moreInfo', 'For details of every cookie we use, read the')}{' '}
            <a
              href="/legal/cookie_policy"
              className="text-teal-600 hover:text-teal-500 underline underline-offset-2"
              data-testid="cookie-settings-policy-link"
            >
              {t('cookies.settings.cookiePolicy', 'Cookie Policy')}
            </a>{' '}
            {t('cookies.settings.and', 'and the')}{' '}
            <a
              href="/legal/privacy_policy"
              className="text-teal-600 hover:text-teal-500 underline underline-offset-2"
              data-testid="cookie-settings-privacy-link"
            >
              {t('cookies.settings.privacyPolicy', 'Privacy Policy')}
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsPage;
